import { AlertFactory } from 'alerts/Alert'
import BillingMethod from 'billing/models/BillingMethod'
import Member from 'iam/models/Member'
import { CancellationToken } from 'infra/cancellation'
import CouponSelectionViewModel from 'jobs/creation/CouponSelectionViewModel'
import { TeamSelection } from 'jobs/creation/DraftChange'
import { QueueChange } from 'jobs/creation/Persister'
import { AlertPort, BillingPort } from 'jobs/creation/ports'
import Job from 'jobs/Job'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { combineLatest, EMPTY, Observable } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { assertNotNil } from 'utils/error-util'
import { fromStream, toStream } from 'utils/mobx-util'
import {
  cancelOnDeactivate,
  filterNonNil,
  shareLatest,
  whileActive,
} from 'utils/rx-util'

export default class BillingViewModel {
  /**
   * The selected billing method, if any.
   */
  @observable private _selectedPaymentMethod: BillingMethod | null = null

  /**
   * Whether to use external billing or not.
   */
  @observable private _bypassBilling = false

  /**
   * Coupon selection.
   */
  readonly couponSelection = new CouponSelectionViewModel(
    this.isActive$,
    this.member$,
    this.job$,
    this.queueChange,
    this.adapter,
    () => this.disabled,
    this.newAlert,
  )

  /**
   * Whether billing is relevant.
   */
  readonly isBillingRelevant$ = toStream(
    () => this.isBillingRelevant,
    true,
  ).pipe(shareLatest(), whileActive(this.isActive$))

  /**
   * The context used to look up the
   * relevant methods.
   *
   * @private
   */
  private readonly context$ = combineLatest([
    this.member$.pipe(filterNonNil()),
    this.team$.pipe(filterNonNil()),
    this.job$,
  ]).pipe(
    map(([member, team, job]): BillingContext => ({ member, team, job })),
    shareLatest(),
  )

  /**
   * Pipeline for fetching the billing info
   * when the relevant stuff becomes available/changes.
   *
   * @private
   */
  private readonly fetchBillingInfo$ = this.context$.pipe(
    switchMap((ctx) =>
      combineLatest([this.isBillingRelevant$, this.canFetchBillingInfo$]).pipe(
        switchMap(([isBillingRelevant, canFetchBillingInfo]) =>
          isBillingRelevant && canFetchBillingInfo
            ? this.fetchBillingInfo(ctx)
            : EMPTY,
        ),
      ),
    ),
    whileActive(this.isActive$),
    shareLatest(),
  )

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly $ = {
    job: fromStream(Job.none, this.job$),
    context: fromStream(null, this.context$),
  }

  constructor(
    private isActive$: Observable<boolean>,
    private member$: Observable<Member | null>,
    private team$: Observable<TeamSelection | null>,
    private job$: Observable<Job>,
    private canFetchBillingInfo$: Observable<boolean>,
    private queueChange: QueueChange,
    private adapter: BillingPort & AlertPort,
    private _isDisabled: () => boolean,
    private _canEditDraftJobsAsAdmin: () => boolean,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
    this.fetchBillingInfo$.subscribe()
  }

  /**
   * Whether to use external billing or not.
   */
  @computed
  get bypassBilling() {
    return this._bypassBilling
  }

  /**
   * The selected payment method if relevant.
   */
  @computed
  get selectedPaymentMethod() {
    return this.shouldCollectPaymentInfo ? this._selectedPaymentMethod : null
  }

  /**
   * Whether the controls for this editor are disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled() || this.isLoading
  }

  /**
   * Whether the billing info is loading.
   */
  @computed
  get isLoading() {
    return this.fetchBillingInfo.pending
  }

  /**
   * Whether we are allowed to change the method.
   */
  @computed
  get canChange() {
    const ctx = this.$.context()
    if (!ctx) {
      return false
    }

    return this.adapter.canChangeBillingMethod(ctx.member, ctx.team)
  }

  /**
   * Whether current member can bypass billing.
   */
  @computed
  get canBypassBilling() {
    if (!this._canEditDraftJobsAsAdmin()) {
      return false
    }

    if (!this.isBillingRelevant) {
      return false
    }

    const ctx = this.$.context()

    return ctx ? ctx.job.owner !== ctx.member : false
  }

  /**
   * Whether billing is relevant.
   */
  @computed
  get isBillingRelevant() {
    const job = this.$.job()
    return job.isBillingEnabled && job.canSubmit
  }

  /**
   * Whether we should collect payment info.
   */
  @computed
  get shouldCollectPaymentInfo() {
    return this.isBillingRelevant && !this.$.job().deferPaymentInfoCollection
  }

  /**
   * Whether the billing selection is valid.
   */
  @computed
  get isValid() {
    if (!this.shouldCollectPaymentInfo) {
      return true
    }

    return Boolean(this.selectedPaymentMethod)
  }

  /**
   * Fetches coupon info.
   *
   * @param couponIds
   * @param ct
   */
  fetchCouponInfo(couponIds: string[], ct: CancellationToken) {
    return this.couponSelection.fetchCouponInfo(couponIds, ct)
  }

  /**
   * Fetches billing information for the specified member/team.
   */
  fetchBillingInfo = task.resolved(async ({ member, team }: BillingContext) => {
    await this.adapter.fetchBillingProfile(member, team)
    // Since we don't currently save the billing method on the Job,
    // we need to select the billing method that is the current default.
    const defaultMethod =
      this.adapter.billingMethodsFor(member, team).find((m) => m.default) ??
      null
    this.setMethod(defaultMethod)
  })

  /**
   * Selects a Billing Method.
   */
  selectMethod = task.resolved(async () => {
    const ctx = this.$.context()
    if (!ctx) {
      return
    }

    // Shows a modal for selecting a method.
    const cts = cancelOnDeactivate(this.isActive$)
    await this.adapter.selectBillingMethod(
      ctx.member,
      ctx.team,
      this.selectedPaymentMethod,
      this.saveMethod.bind(this),
      cts.token,
    )
    cts.dispose()
  })

  /**
   * Starts the New Payment Method flow.
   */
  newPaymentMethod = task.resolved(async () => {
    const ctx = this.$.context()
    const member = assertNotNil(ctx?.member, 'No workspace selected.')
    const teamSelection = assertNotNil(
      ctx?.team,
      'No team or individual selected.',
    )
    const cts = cancelOnDeactivate(this.isActive$)
    const method = await this.adapter.newBillingMethodFor(
      member,
      teamSelection,
      cts.token,
    )
    cts.dispose()

    if (!method) {
      return
    }

    await this.saveMethod(method)
  })

  /**
   * Sets the billing method.
   * @param method
   */
  @action.bound
  setMethod(method: BillingMethod | null) {
    this._selectedPaymentMethod = method
  }

  /**
   * Saves the billing method.
   *
   * @param method
   * @private
   */
  saveMethod = task.resolved(async (method: BillingMethod) => {
    await this.queueChange({
      type: 'BillingMethodSelected',
      billingMethod: method,
    })
    this.setMethod(method)
  })

  /**
   * Sets external billing option.
   * @param value
   */
  @action.bound
  setBypassBilling(value: boolean) {
    this._bypassBilling = value
  }
}

/**
 * Billing context.
 */
interface BillingContext {
  member: Member
  team: TeamSelection
  job: Job
}
